import React, { useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import type { TenantThemeConfig } from '../types/theme'
import { hasActiveLiveTv, countActiveLiveStreams } from '../lib/liveTv'
import AssistiveFloatChrome from './AssistiveFloatChrome'

interface DraggableLiveTvFloatProps {
  themeConfig: TenantThemeConfig
  bottomOffset?: number
}

const FLOAT_SIZE = 58

function storageKey(tenantId: string) {
  return `live-tv-float-pos:${tenantId}`
}

function getDefaultPosition(_floatSize: number, bottomOffset: number) {
  if (typeof window === 'undefined') return { x: 16, y: 100 }
  return {
    x: 16,
    y: Math.max(16, window.innerHeight - FLOAT_SIZE - bottomOffset),
  }
}

const DraggableLiveTvFloat: React.FC<DraggableLiveTvFloatProps> = ({
  themeConfig,
  bottomOffset = 88,
}) => {
  const navigate = useNavigate()
  const channels = themeConfig.settings.liveTvChannels || []
  const huayYoutubeLive = themeConfig.settings.huayYoutubeLive
  const visible = hasActiveLiveTv(channels, themeConfig.settings.liveTvEnabled, huayYoutubeLive)

  const liveCount = useMemo(
    () => countActiveLiveStreams(channels, themeConfig.settings.liveTvEnabled, huayYoutubeLive),
    [channels, themeConfig.settings.liveTvEnabled, huayYoutubeLive]
  )

  if (!visible) return null

  return (
    <AssistiveFloatChrome
      storageKey={storageKey(themeConfig.tenantId)}
      floatSize={FLOAT_SIZE}
      bottomOffset={bottomOffset}
      getDefaultPosition={getDefaultPosition}
      onTap={() => navigate('/live-tv')}
      ariaLabel="เปิดหน้าดูถ่ายทอดสด"
      dockAriaLabel="ซ่อนปุ่ม LIVE ไว้ขอบจอ"
      undockAriaLabel="แสดงปุ่ม LIVE"
      hideTitle="ซ่อนปุ่ม LIVE"
      dockBackground="linear-gradient(180deg, #ef4444 0%, #b91c1c 100%)"
      zIndex={9998}
      dockedChildren={(
        <>
          <i className="fas fa-tv" style={{ fontSize: 16 }} />
          <span style={{ fontSize: 9, fontWeight: 800, letterSpacing: 0.6 }}>LIVE</span>
        </>
      )}
    >
      <div
        style={{
          width: '100%',
          height: '100%',
          borderRadius: '9999px',
          position: 'relative',
          background: 'linear-gradient(135deg, #ef4444 0%, #b91c1c 100%)',
          boxShadow: '0 10px 28px rgba(239, 68, 68, 0.45)',
          color: '#fff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <span
          style={{
            position: 'absolute',
            top: 6,
            right: 6,
            width: 10,
            height: 10,
            borderRadius: '50%',
            background: '#fff',
            boxShadow: '0 0 0 0 rgba(255,255,255,0.8)',
            animation: 'liveTvPulse 1.4s infinite',
          }}
        />
        <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', lineHeight: 1.1 }}>
          <i className="fas fa-tv" style={{ fontSize: 18, marginBottom: 2 }} />
          <span style={{ fontSize: 9, fontWeight: 800, letterSpacing: 0.6 }}>LIVE</span>
        </span>
        {liveCount > 1 && (
          <span
            style={{
              position: 'absolute',
              left: -4,
              top: -4,
              minWidth: 18,
              height: 18,
              borderRadius: 999,
              background: '#111827',
              color: '#fff',
              fontSize: 10,
              fontWeight: 700,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '0 4px',
            }}
          >
            {liveCount}
          </span>
        )}
        <style>{`
          @keyframes liveTvPulse {
            0% { box-shadow: 0 0 0 0 rgba(255,255,255,0.7); }
            70% { box-shadow: 0 0 0 8px rgba(255,255,255,0); }
            100% { box-shadow: 0 0 0 0 rgba(255,255,255,0); }
          }
        `}</style>
      </div>
    </AssistiveFloatChrome>
  )
}

export default DraggableLiveTvFloat
